import React from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface EditModalProps {
  title: string;
  onClose: () => void;
  onSave: () => void;
  saving?: boolean;
  error?: string | null;
  canSave?: boolean;
  children: React.ReactNode;
  saveLabel?: string;
  wide?: boolean;
}

export const EditModal: React.FC<EditModalProps> = ({
  title,
  onClose,
  onSave,
  saving = false,
  error = null,
  canSave = true,
  children,
  saveLabel,
  wide = false,
}) => {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className={`bg-white rounded-lg shadow-xl w-full max-h-[90vh] overflow-y-auto ${wide ? 'max-w-3xl' : 'max-w-xl'}`}>
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label={t('common.buttons.close', {}, 'Lukk')}>
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
              {error}
            </div>
          )}
          {children}
        </div>

        <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded font-medium"
          >
            {t('common.buttons.cancel', {}, 'Avbryt')}
          </button>
          <button
            onClick={onSave}
            disabled={saving || !canSave}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-medium px-4 py-2 rounded flex items-center gap-2"
          >
            {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
            {saveLabel ?? t('common.buttons.save', {}, 'Lagre')}
          </button>
        </div>
      </div>
    </div>
  );
};

interface FormFieldProps {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}

export const FormField: React.FC<FormFieldProps> = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-600">*</span>}
    </label>
    {children}
  </div>
);

export const inputClasses =
  'w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
